import { HvacArea, HvacGroup } from '@belimo-retrofit-portal/logic';
import { pipe } from 'fp-ts/function';
import * as A from 'fp-ts/ReadonlyArray';
import { ReadonlyRecord } from 'fp-ts/ReadonlyRecord';
import { createSelector } from 'reselect';
import { HVAC_GROUP_AREAS, HVAC_GROUP_LIST } from 'src/modules/hvac/constants/group';
import { getHvacAssesmentFormData } from 'src/modules/hvac/selectors/getHvacAssesmentFormData';
import { getHvacSchema } from 'src/modules/hvac/selectors/getHvacSchema';
import { HvacFormAreaData, HvacFormSectionData, HvacFormVariantData } from 'src/modules/hvac/types/HvacFormData';
import { getReportFormData } from 'src/modules/report/selectors/getReportFormData';
import {
  ReportGeneratedHvacArea,
  ReportGeneratedHvacGroup,
  ReportGeneratedHvacSection,
} from 'src/modules/report/types/ReportGenerated';

export const getReportGeneratedHvac = createSelector([
  getReportFormData,
  getHvacSchema,
  getHvacAssesmentFormData,
], (reportFormData, hvacSchema, hvacFormData): ReadonlyArray<ReportGeneratedHvacGroup> => pipe(
  HVAC_GROUP_LIST,
  A.map((groupKey) => {
    const reportGroups = reportFormData.hvac.filter((it) => it.group === groupKey);

    return pipe(
      hvacFormData.groups[groupKey],
      A.mapWithIndex((groupIndex, groupData): ReportGeneratedHvacGroup => ({
        id: `${groupKey}-${groupIndex}`,
        number: groupIndex + 1,

        group: groupKey,
        empty: isGroupEmpty(groupKey, hvacFormData.areas),
        enabled: reportGroups[groupIndex].enabled,

        title: groupData.title,
        areas: pipe(
          HVAC_GROUP_AREAS[groupKey],
          A.filter((areaKey) => reportGroups[groupIndex].showAreas[areaKey]),
          A.map((areaKey): ReportGeneratedHvacArea => ({
            area: areaKey,
            comment: groupData.areas[areaKey].comment,
            sections: groupData.areas[areaKey].sections.map(mapSection),
          })),
        ),
        schemas: hvacSchema[groupKey][groupIndex],

        showDetails: reportGroups[groupIndex].showDetails,
        showSchemas: reportGroups[groupIndex].showSchemas,
        showComments: reportGroups[groupIndex].showComments,
      })),
    );
  }),
  A.flatten,
));

function isGroupEmpty(
  group: HvacGroup,
  areas: ReadonlyRecord<HvacArea, HvacFormAreaData>,
): boolean {
  return HVAC_GROUP_AREAS[group].every((area) => !areas[area].enabled);
}

function mapSection(section: HvacFormSectionData): ReportGeneratedHvacSection {
  return {
    config: section.config,
    actual: {
      rating: section.answer.actual.option?.class ?? null,
      title: section.answer.actual.option?.title ?? null,
      explanation: section.answer.actual.option?.explanation ?? null,
      comment: getComment(section.answer.actual),
    },
    future: {
      rating: section.answer.future.option?.class ?? null,
      title: section.answer.future.option?.title ?? null,
      explanation: section.answer.future.option?.explanation ?? null,
      comment: getComment(section.answer.future),
    },
  };
}

function getComment(answer: HvacFormVariantData): string {
  const value = answer.additionals.find((it) => it.config.id === 'comment')?.value;
  return typeof value === 'string' ? value : '';
}
